import { motion } from "framer-motion";
import { useState, useEffect } from "react";

export default function LaunchAnnouncement() {
  const [countdown, setCountdown] = useState(10);
  const [autoRedirect, setAutoRedirect] = useState(true);

  // Redirection automatique après 10 secondes
  useEffect(() => {
    if (!autoRedirect) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          window.location.href = 'https://jinkasan.com';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [autoRedirect]);

  // Animation des confettis
  const confettiVariants = {
    initial: { opacity: 0, y: -100, rotate: 0 },
    animate: (i: number) => ({
      opacity: [0, 1, 1, 0],
      y: [0, 100, 200, 300],
      rotate: [0, 180, 360],
      x: Math.sin(i) * 100,
      transition: {
        duration: 3,
        repeat: Infinity,
        delay: i * 0.2,
      }
    })
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#181818] via-[#2A2A2A] to-[#333] flex flex-col items-center justify-center" style={{ fontFamily: 'Verdana, sans-serif' }}>
      {/* Confettis animés */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            custom={i}
            variants={confettiVariants}
            initial="initial"
            animate="animate"
            className="absolute w-3 h-3 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: -20,
              backgroundColor: ['#DD442E', '#FACE31', '#4B9C5B', '#FF6B59'][i % 4]
            }}
          />
        ))}
      </div>

      {/* Background avec overlay */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url("/images/hero-bg.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Contenu principal */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Badge de lancement */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-2 rounded-lg border border-white/20">
            <span className="text-white font-medium text-sm tracking-wide">LANCEMENT OFFICIEL</span>
          </div>
        </motion.div>

        {/* Titre principal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-tight mb-6">
            <span className="block mb-2">JINKASAN</span>
            <span className="block bg-clip-text text-transparent bg-gradient-to-r from-[#FACE31] via-[#FDB31A] to-[#FACE31]">
              EST MAINTENANT
            </span>
            <span className="block mt-2">EN LIGNE !</span>
          </h1>

        </motion.div>

        {/* CTA principal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="flex flex-col items-center gap-6"
        >
          <motion.a
            href="https://jinkasan.com"
            className="group bg-gradient-to-r from-[#DD442E] to-[#E65142] text-white rounded-2xl py-5 px-12 text-xl font-bold shadow-2xl hover:shadow-[#DD442E]/50 transition-all relative overflow-hidden flex items-center justify-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              className="absolute inset-0 bg-white/20"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.5 }}
            />
            <span className="relative z-10">Découvrir Jinkasan</span>
          </motion.a>

          {/* Compteur de redirection */}
          {autoRedirect && countdown > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="flex items-center gap-3 text-white/70 text-sm"
            >
              <span>Redirection automatique dans</span>
              <span className="bg-white/10 px-3 py-1 rounded-full font-bold text-[#FACE31]">
                {countdown}s
              </span>
              <button
                onClick={() => setAutoRedirect(false)}
                className="underline hover:text-white transition-colors"
              >
                Annuler
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* Message de remerciement */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="text-center mt-16 max-w-2xl mx-auto"
        >
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-4">
              Merci à nos 500 testeurs ! 🙏
            </h3>
            <p className="text-white/80 leading-relaxed">
              Grâce à vos retours précieux, nous avons pu améliorer la plateforme. 
              Vos bons d'achat sont toujours disponibles dans votre compte. 
              Bonne découverte et bon shopping groupé !
            </p>
          </div>
        </motion.div>
      </div>

      {/* Footer minimaliste */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="relative z-10 w-full text-center py-8 text-white/50 text-sm"
      >
        <p>© 2025 Jinkasan - L'achat groupé au Burkina Faso</p>
      </motion.div>
    </section>
  );
}
